import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid, Chip } from '@material-ui/core'
import SydLabel from '../../../../../../../../commonDesign/SydLabel'
import SydSelect from '../../../../../../../../commonDesign/SydSelect'
import SydInput from '../../../../../../../../commonDesign/SydInput'
import SydButton from '../../../../../../../../commonDesign/Button'

const tooltipTriggerOptions = [
  { label: 'Item', value: 'item' },
  { label: 'Axis', value: 'axis' }
]

function TreeMapEditor ({ config, columns, onChange }) {
  const [selectedColumn, setSelectedColumn] = useState('')

  const columnOptions = useMemo(() => {
    return [
      { label: 'Select a column...', value: '' },
      ...columns.map(col => ({ label: col.name, value: col.name }))
    ]
  }, [columns])

  const availableForIndex = useMemo(() => {
    const used = new Set(config.indexBy || [])
    return [
      { label: 'Select a column to add...', value: '' },
      ...columns.filter(col => !used.has(col.name)).map(col => ({ label: col.name, value: col.name }))
    ]
  }, [columns, config.indexBy])

  const handleChange = useCallback((field, value) => {
    onChange({ [field]: value })
  }, [onChange])

  const handleAddIndex = useCallback(() => {
    if (!selectedColumn) return
    onChange({ indexBy: [...(config.indexBy || []), selectedColumn] })
    setSelectedColumn('')
  }, [onChange, config.indexBy, selectedColumn])

  const handleRemoveIndex = useCallback((col) => {
    onChange({ indexBy: (config.indexBy || []).filter(c => c !== col) })
  }, [onChange, config.indexBy])

  const handleTooltipChange = useCallback((field, value) => {
    onChange({ tooltip: { ...(config.tooltip || {}), [field]: value || null } })
  }, [onChange, config.tooltip])

  return (
    <Grid container spacing={2}>
      {/* Index By (hierarchy levels) */}
      <Grid item xs={12}>
        <SydLabel required label='Group By (hierarchy levels)' helperText='Order matters: first column is the top level'>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
            <SydSelect
              minWidth='0'
              options={availableForIndex}
              value={selectedColumn}
              onChange={(e) => setSelectedColumn(e.target.value)}
              style={{ maxWidth: '240px' }}
            />
            <SydButton size='sm' variant='primary' onClick={handleAddIndex} disabled={!selectedColumn}>
              Add Level
            </SydButton>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {(config.indexBy || []).map((col, idx) => (
              <Chip
                key={col}
                label={`${idx + 1}. ${col}`}
                onDelete={() => handleRemoveIndex(col)}
                variant='outlined'
                size='small'
              />
            ))}
            {(config.indexBy || []).length === 0 && (
              <span style={{ color: 'var(--text-color-subtle)', fontSize: '0.875rem' }}>
                No hierarchy levels defined
              </span>
            )}
          </div>
        </SydLabel>
      </Grid>

      <Grid item xs={6} md={4}>
        <SydLabel required label='Value Column'>
          <SydSelect
            minWidth='0'
            options={columnOptions}
            value={config.valueAccessor || ''}
            onChange={(e) => handleChange('valueAccessor', e.target.value || null)}
          />
        </SydLabel>
      </Grid>
      <Grid item xs={6} md={4}>
        <SydLabel label='Color Column'>
          <SydSelect
            minWidth='0'
            options={columnOptions}
            value={config.colorAccessor || ''}
            onChange={(e) => handleChange('colorAccessor', e.target.value || null)}
          />
        </SydLabel>
      </Grid>
      <Grid item xs={6} md={4}>
        <SydLabel label='Root Label'>
          <SydInput
            minWidth='0'
            value={config.rootLabel || 'All'}
            onChange={(e) => handleChange('rootLabel', e.target.value || 'All')}
            placeholder='All'
          />
        </SydLabel>
      </Grid>
      <Grid item xs={6} md={3}>
        <SydLabel label='Tooltip Trigger'>
          <SydSelect
            minWidth='0'
            options={tooltipTriggerOptions}
            value={config.tooltip?.trigger || 'item'}
            onChange={(e) => handleTooltipChange('trigger', e.target.value)}
          />
        </SydLabel>
      </Grid>
      <Grid item xs={6} md={3}>
        <SydLabel label='Tooltip Value Format'>
          <SydInput
            minWidth='0'
            value={config.tooltip?.valueFormat || ''}
            onChange={(e) => handleTooltipChange('valueFormat', e.target.value)}
            placeholder='marketValue'
          />
        </SydLabel>
      </Grid>
    </Grid>
  )
}

TreeMapEditor.propTypes = {
  config: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired
}

export default TreeMapEditor
